import { centeredPopoverRootStyle } from "components/Popover/Popover";
import {
    NAVIGATION_PRIORITIES,
    useNavigationPrompt,
} from "components/Prompt/NavigationPromptContext";
import { Location } from "history";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Prompt, useHistory } from "react-router-dom";
import { store } from "store";
import { ModalPrompt } from "./ModalPrompt";

/**
 * App-global guard that blocks tab-close / in-app navigation while an
 * action-button request is in flight.
 *
 * Why: an action button (plugin_action call, Save, Migrate deprecations, run,
 * delete, …) fires an async request from the Console. If the user closes/reloads
 * the tab *before that request reaches the backend*, the XHR is torn down and the
 * action never happens (for a plugin_action, its execution is never created — so
 * there is nothing to cancel; it simply did not start). It is a race: close after
 * the response returned → the action ran; close too early → it did not. This
 * guard closes the race window by blocking navigation until the request settles
 * (its response — hence the created execution id — has returned).
 *
 * Mechanism: the axios interceptors in api/restClient.ts increment/decrement a
 * global `pendingActions.count` for every request marked `trackAction: true`.
 * Here we arm a `beforeunload` guard (true tab-close/reload — only the generic
 * native browser prompt is possible there) and a React-Router in-app navigation
 * blocker (a custom modal) whenever that count > 0.
 *
 * The "Leave anyway" button just abandons the UI navigation; it does not cancel
 * anything server-side (there is nothing running yet during the request window).
 */

export const GUARD_MESSAGE =
    "An action is still being submitted. If you leave now it might not start. " +
    "Go back to wait for it to finish, or leave anyway.";

export const ActionInFlightGuard = () => {
    const history = useHistory();
    const { priority: currentPriority, setPriority } = useNavigationPrompt();
    const priority = NAVIGATION_PRIORITIES.ACTION_IN_FLIGHT;

    const pendingCount = useSelector(
        (state: any) => state?.pendingActions?.count ?? 0
    );
    const shouldBlockNavigation = pendingCount > 0;

    const [isBlocking, setIsBlocking] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [lastLocation, setLastLocation] = useState<Location | null>(null);
    const [confirmedNavigation, setConfirmedNavigation] = useState(false);

    // Only actually block in-app navigation when we are the highest-priority
    // prompt (the shared priority context arbitrates against form prompts).
    const shouldShowPrompt = isBlocking && currentPriority === priority;

    useEffect(() => {
        if (shouldBlockNavigation) {
            if (priority >= currentPriority) {
                setPriority(priority);
                setIsBlocking(true);
            }
        } else {
            if (currentPriority === priority) {
                setPriority(0);
            }
            setIsBlocking(false);
        }

        return () => {
            if (currentPriority === priority) {
                setPriority(0);
            }
        };
    }, [shouldBlockNavigation, priority, currentPriority, setPriority]);

    // beforeunload uses a ref so the listener always sees the current blocking
    // state without needing to be re-registered on every change.
    const isBlockingRef = useRef(isBlocking);
    useEffect(() => {
        isBlockingRef.current = isBlocking;
    }, [isBlocking]);

    const onTabCloseOrRefresh = useCallback((event) => {
        if (isBlockingRef.current) {
            event.preventDefault();
            // Modern browsers ignore custom text and show a generic prompt, but
            // returnValue must be set (to any truthy value) to trigger it.
            event.returnValue = GUARD_MESSAGE;
            return GUARD_MESSAGE;
        }
    }, []);

    useEffect(() => {
        window.addEventListener("beforeunload", onTabCloseOrRefresh);
        return () => {
            window.removeEventListener("beforeunload", onTabCloseOrRefresh);
        };
    }, [onTabCloseOrRefresh]);

    const handleBlockedNavigation = useCallback(
        (nextLocation: Location): boolean => {
            // Read the LIVE pending-action count from the store, not the value
            // from the last committed render. An action button's own
            // post-completion navigation (e.g. "test connection" → navigate to
            // the created execution in PluginListAction) fires in the same
            // microtask flush as the request's DECREMENT, before React re-renders
            // this guard — so the render-time flag (`shouldShowPrompt`) is still
            // stale-true even though nothing is in flight any more. Reading the
            // store directly reflects the decrement that already ran, so a
            // navigation that begins when nothing is actually in flight is never
            // blocked by a spurious "an action is still being submitted" prompt.
            const livePendingCount = store.getState()?.pendingActions?.count ?? 0;
            if (livePendingCount === 0) {
                return true;
            }
            if (!confirmedNavigation && shouldShowPrompt) {
                setModalVisible(true);
                setLastLocation(nextLocation);
                return false;
            }
            return true;
        },
        [confirmedNavigation, shouldShowPrompt]
    );

    const onGoBack = useCallback(() => {
        setModalVisible(false);
        setConfirmedNavigation(false);
    }, []);

    const onLeaveAnyway = useCallback(() => {
        setModalVisible(false);
        setConfirmedNavigation(true);
    }, []);

    useEffect(() => {
        if (confirmedNavigation && lastLocation) {
            history.push({
                pathname: lastLocation.pathname,
                search: lastLocation.search || "",
            });
            setConfirmedNavigation(false);
        }
    }, [confirmedNavigation, lastLocation, history]);

    return (
        <>
            <Prompt when={shouldShowPrompt} message={handleBlockedNavigation} />
            <ModalPrompt
                isOpen={modalVisible}
                titleText={GUARD_MESSAGE}
                cancelButtonText="Go back"
                confirmButtonText="Leave anyway"
                confirmButtonVariation="danger"
                onCancel={onGoBack}
                onConfirm={onLeaveAnyway}
                rootStyle={{ ...centeredPopoverRootStyle, zIndex: 20000 }}
            />
        </>
    );
};
